import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { fetchObservations, type Observation } from '../../api/work';
import './pulse-panels.css';

/**
 * The raw tracked facts — what an interpreter noticed, before anything
 * narrated it.
 *
 * One tab below `People` in the conclusions-first order: this is where you
 * land when a person's series looks wrong and you want the finding that
 * produced it rather than the sentence about it.
 *
 * **`value: null` is ambiguous and the panel says so.** The route coerces
 * a non-finite float to `null` because `JSONResponse` renders with
 * `allow_nan=False` and one `inf` would 500 the whole response — so a
 * null value means "not measured" *or* "refused to serialise", and those
 * cannot be told apart from here. Rendered as `–` with that on hover
 * rather than as a zero.
 */

const STATUSES = ['open', 'resolved', 'all'] as const;

function relative(ms: number | null): string {
  if (ms === null) return '—';
  const min = Math.floor((Date.now() - ms) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function num(v: number | null): string {
  if (v === null) return '–';
  if (Number.isInteger(v)) return String(v);
  return Math.abs(v) < 10 ? v.toFixed(2) : v.toFixed(1);
}

/** `person:Andrew` → `Andrew`, keeping the kind as a separate token. */
function splitSubject(subject: string): { kind: string; name: string } {
  const i = subject.indexOf(':');
  return i < 0
    ? { kind: '', name: subject }
    : { kind: subject.slice(0, i), name: subject.slice(i + 1) };
}

export function ObservationsPanel() {
  const [rows, setRows] = useState<Observation[] | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('open');
  const [kind, setKind] = useState('');
  // The predecessor filtered by pattern and by metric name and this did
  // not. Both are client-side: the route returns one page and the filters
  // narrow it, which is honest as long as the page cap is shown — see the
  // count in the bar below.
  const [pattern, setPattern] = useState('');
  const [metric, setMetric] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(() => {
    setRows(null);
    setError(null);
    fetchObservations(status)
      .then((r) => {
        setRows(r.observations);
        setLimit(r.limit);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, [status]);

  useEffect(load, [load]);

  const kinds = useMemo(
    () =>
      [...new Set((rows ?? []).map((r) => splitSubject(r.subject).kind))]
        .filter(Boolean)
        .sort(),
    [rows],
  );

  const patterns = useMemo(
    () =>
      [...new Set((rows ?? []).map((r) => r.pattern).filter(Boolean))].sort(),
    [rows],
  ) as string[];

  const visible = useMemo(() => {
    const needle = metric.trim().toLowerCase();
    return (rows ?? []).filter(
      (r) =>
        (!kind || splitSubject(r.subject).kind === kind) &&
        (!pattern || r.pattern === pattern) &&
        (!needle || (r.metric_name ?? '').toLowerCase().includes(needle)),
    );
  }, [rows, kind, pattern, metric]);

  return (
    <div className="pp">
      <div className="pp__bar">
        <div className="pp__chips" role="group" aria-label="Status">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`pp__chip${status === s ? ' is-active' : ''}`}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
        {kinds.length > 1 && (
          <label className="pp__filter">
            <span>subject</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">any</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
        )}
        {patterns.length > 1 && (
          <label className="pp__filter">
            <span>pattern</span>
            <select
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
            >
              <option value="">any pattern</option>
              {patterns.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
        )}
        <input
          className="pp__input"
          placeholder="filter by metric name…"
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
        />
        <span className="pp__dim">
          {rows === null
            ? 'loading…'
            : `${visible.length}${
                limit !== null && rows.length >= limit
                  ? ` of at least ${limit} — the route caps the page`
                  : ''
              }`}
        </span>
        <button type="button" className="pp__btn" onClick={load}>
          Refresh
        </button>
      </div>

      {error && <p className="pp__error">{error}</p>}

      {rows === null ? (
        <p className="pp__dim">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="pp__dim">
          No {status === 'all' ? '' : status} observations
          {kind && ` for ${kind}`}
          {pattern && ` matching ${pattern}`}
          {metric && ` with a metric like "${metric}"`}. An interpreter
          writes one when a metric
          crosses a threshold it watches, so an empty list means nothing has
          crossed — not that nothing is measured.
        </p>
      ) : (
        <div className="pp__scroll">
          <table className="pp__table">
            <thead>
              <tr>
                <th>subject</th>
                <th>pattern</th>
                <th>metric</th>
                <th className="pp__num">value</th>
                <th className="pp__num">threshold</th>
                <th>status</th>
                <th>last seen</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => {
                const s = splitSubject(o.subject);
                const isOpen = open === o.id;
                return (
                  // The key belongs on the Fragment, not on the rows
                  // inside it: React reconciles what `map` returns, and a
                  // keyed child of an unkeyed fragment is still a list
                  // item with no key.
                  <Fragment key={o.id}>
                    <tr
                      className="pp__row"
                      onClick={() => setOpen(isOpen ? null : o.id)}
                    >
                      <td>
                        {s.kind && <span className="pp__tag">{s.kind}</span>}
                        <span className="pp__strong">{s.name}</span>
                      </td>
                      <td>{o.pattern ?? '—'}</td>
                      <td className="pp__mono">{o.metric_name ?? '—'}</td>
                      <td
                        className="pp__num"
                        title={
                          o.value === null
                            ? 'null: not measured, or a non-finite float the route refused to serialise — these cannot be distinguished here'
                            : undefined
                        }
                      >
                        {num(o.value)}
                      </td>
                      <td className="pp__num">{num(o.threshold)}</td>
                      <td>
                        <span
                          className={`pp__badge pp__badge--${o.status.toLowerCase()}`}
                        >
                          {o.status.toLowerCase()}
                        </span>
                      </td>
                      <td className="pp__dim">{relative(o.last_seen_at_ms)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} className="pp__detail">
                          <dl className="pp__meta">
                            <dt>interpreter</dt>
                            <dd>
                              {o.interpreter_name ?? '—'}
                              {o.interpreter_version !== null &&
                                ` v${o.interpreter_version}`}
                            </dd>
                            <dt>fingerprint</dt>
                            <dd>
                              <code>{o.fingerprint}</code>
                            </dd>
                            <dt>first seen</dt>
                            <dd>{relative(o.first_seen_at_ms)}</dd>
                            {o.resolved_at_ms !== null && (
                              <>
                                <dt>resolved</dt>
                                <dd>{relative(o.resolved_at_ms)}</dd>
                              </>
                            )}
                            {o.last_value !== null && (
                              <>
                                <dt>previous value</dt>
                                <dd>{num(o.last_value)}</dd>
                              </>
                            )}
                          </dl>
                          <pre className="pp__pre">
                            {JSON.stringify(
                              { evidence: o.evidence, params: o.params },
                              null,
                              2,
                            )}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
